








































































import { Component, Vue, Prop, Watch, Mixins } from 'vue-property-decorator';
import { Mutations, Actions } from '@/ts/store/Constants';
import ConversationData from '@/ts/ConversationData';
import PersonData from '@/ts/PersonData';
import MessageData from '@/ts/MessageData';
import Message from '@/components/Messages/Feed/Message.vue';
import FeedHeader from '@/components/Messages/Feed/Header.vue';
import HorizontalParticipantIcons from '@/components/Messages/HorizontalParticipantIcons.vue';
import { mapGetters } from 'vuex';
import DeviceMethods from '@/mixins/DeviceMethods.vue';
import InputFieldMethods from '@/mixins/InputFieldMethods.vue';

import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Keyboard, KeyboardInfo } from '@capacitor/keyboard';

@Component({
    computed: {
        ...mapGetters({
            hasNewImageData: 'Camera/hasNewImageData',
        }),
    },
    components: {
        Message,
        FeedHeader,
        HorizontalParticipantIcons,

    },
})
export default class Feed extends Mixins(
    DeviceMethods,
    InputFieldMethods,
) {
    // Props
    @Prop() public conversation!: ConversationData;
    public deviceInfo: any = null;
    private scrollElement: any | null = null;
    private hasNewImageData: boolean;
    private fileData: File;
    private keyboardListenerAdded: boolean = false;
    private keyboardVisible: boolean = false;
    private adminAttachment64: string = null;
    private pdfAttachment: any = null;

    constructor() {
        super();

        // Set up keyboardDidShow listener
        this.initializeKeyboardListener();
    }

    get messageList(): MessageData[] {
        // Slice(0) is necessary because TS sort is in-place
        const clone = this.conversation.messageList.slice(0);

        // Return a time descending sorted version of the cloned array
        return clone.sort((firstMessage, secondMessage) => {
            if (firstMessage.sortingCriterion > secondMessage.sortingCriterion) {
                return 1;
            }

            if (firstMessage.sortingCriterion < secondMessage.sortingCriterion) {
                return -1;
            }

            return 0;
        });

        return clone;
    }

    get filteredMessages() {
        const messages: MessageData[] = this.conversation.messageList;
        return messages.filter((message) => {
            return message.getSearchableText().indexOf(this.messageSearchTerms.toLowerCase()) > -1;
        });
    }

    get messageSearchTerms(): string {
        return this.$store.getters['Messages/getConversationSearchTerm'];
    }


    get readyToSend(): boolean {
        return (this.conversation.participants.length > 1
            && (this.currentMessage.length > 0 || this.hasNewImageData)
            && !this.$store.getters['Messages/isWaitingForResponse']
        );
    }

    get sendButtonClasses(): object {
        return {
            'hococo-inactive': !(this.readyToSend),
        };
    }

    get placeholder(): string {
        return (this.hasNewImageData)
            ? Vue.i18n.translate('messages.image-captured', 'Image captured, click to send...')
            : Vue.i18n.translate('messages.write-message-here', 'Write message here...');
    }

    get currentMessage(): string {
        return this.$store.getters['Messages/getCurrentMessage'];
    }

    set currentMessage(value: string) {
        // this.$store.commit(Mutations.UPDATE_CURRENT_MESSAGE, value);
        return;
    }

    get dataLoaded(): boolean {
        return (
            this.conversation !== null &&
            this.conversation.participants !== null &&
            this.conversation.messages !== null
        );
    }

    get imageData(): string {
        return this.$store.getters['Camera/capturedImageData'];
    }

    get previewStyles(): string {
        if (!this.imageData) {
            return '';
        }

        const width = this.$store.state.Viewport.width * 0.30;
        const ratio = this.$store.state.Camera.metaData.width / this.$store.state.Camera.metaData.height;
        const height = width * (1.0 / ratio);

        let styleString = 'width: ' + width + 'px; ';
        styleString += 'height: ' + height + 'px; ';
        styleString += 'top: -' + (height + 20) + 'px;';

        return styleString;
    }

    get controlClasses(): object {
        return {
            'hococo-keyboard-visible': this.keyboardVisible,
        };
    }

    public mounted() {
        this.scrollElement = document.getElementById('hococo-messages-overflow');
        this.textInputElement = (this.$refs['message-text'] as HTMLTextAreaElement);
        this.goToBottom(0);
    }

    public getUser(userId: number): PersonData {
        return this.$store.getters['User/getCacheUser'](userId);
    }

    public ignoreEvent(event: Event): void {
        event.preventDefault();
        event.stopPropagation();
    }

    public handleSendEvent(event: Event): void {
        event.preventDefault();
        event.stopPropagation();
        this.sendMessage();
    }

    public handleMessageInput(event: KeyboardEvent): void {
        this.$store.commit(Mutations.UPDATE_CURRENT_MESSAGE, this.textInputElement.value);
        this.setInputFieldHeight();
    }

    public goToBottom(timeout: number = 250): void {
        setTimeout(() => {
            Vue.nextTick(() => {
                if (typeof this.$route.params.messageId !== 'undefined') {
                    this.scrollToMessage(parseInt(this.$route.params.messageId, 10));
                } else {
                    if (this.scrollElement !== null) {
                        this.scrollToBottom(true);
                    }
                }
            });
        }, timeout);
    }

    public blurMessageInput(event: Event) {
        this.textInputElement.blur();
    }

    public setInputFieldHeight() {
        this.textInputElement.style.height = 'inherit';

        // Get the computed styles for the element
        const computed = window.getComputedStyle(this.textInputElement);

        // Calculate the height
        const height = parseInt(computed.getPropertyValue('border-top-width'), 10)
                    + parseInt(computed.getPropertyValue('padding-top'), 10)
                    + this.textInputElement.scrollHeight
                    + parseInt(computed.getPropertyValue('padding-bottom'), 10)
                    + parseInt(computed.getPropertyValue('border-bottom-width'), 10) + 1;

        this.textInputElement.style.height = height + 'px';
    }

    public resetInputField() {
        this.textInputElement.value = '';
        this.setInputFieldHeight();
        this.removeAdminAttachment();
        this.removeAdminAttachmentPdf();
    }

    public sendMessage(): void {
        if (this.readyToSend) {
            this.$store.dispatch(Actions.SEND_MESSAGE)
                .then(() => {
                    this.resetInputField();
                });
        }
    }

    public showCameraDialog(): void {
        this.device().then((deviceInfo) => {
            if (deviceInfo.platform === 'web') {
                this.$store.commit(Mutations.SET_PHOTO_RECIPIENT, 'messages');
                this.showFileInputDialog();
            } else {
                this.$store.commit(Mutations.SET_PHOTO_RECIPIENT, 'messages');
                this.requestMobileImage();
            }
        });
    }

    public showFileInputDialog(): void {
        (this.$refs.fileInputElement as HTMLInputElement).click();
    }

    public showPdfInputDialog(): void {
        (this.$refs.pdfInputElement as HTMLInputElement).click();
    }

    public messageInputVisible(status: string) {
        const user = this.$store.getters['User/getUser'];
        return user.isTenant ? status === 'open' : true;
    }

    public handleFileChange(event: Event) {
        this.fileData = (event.target as HTMLInputElement).files[0];
        if (typeof this.fileData === 'undefined') {
            this.removeAdminAttachment();
            return;
        }

        if (this.fileData !== null) {
            const reader = new FileReader();

            // Read the contents of Image File.
            reader.readAsDataURL(this.fileData);
            reader.onload = (e: any) => {
                this.removeAdminAttachmentPdf();
                this.adminAttachment64 = e.target.result;

                let tempImage = new Image();
                const canvas = document.createElement('canvas');
                const context = canvas.getContext('2d');

                tempImage.onload = () => {
                    const width = 1024;
                    const widthProportion = width / tempImage.width;
                    const height = tempImage.height * widthProportion;
                    canvas.width = width;
                    canvas.height = height;
                    context.drawImage(tempImage, 0, 0, width, height);

                    tempImage = null;

                    this.$store.commit(Mutations.SET_MESSAGE_ADMIN_ATTACHMENT, {
                        meta: {
                            width,
                            height,
                        },
                        data: canvas.toDataURL('image/jpeg'),
                    });
                };

                tempImage.src = e.target.result;

            };
        } else {
            this.removeAdminAttachment();
        }
    }

    public handlePdfChange(event: Event) {
        const pdf = (event.target as HTMLInputElement).files[0];
        if (!pdf) {
            this.removeAdminAttachmentPdf();
            return;
        }
        this.pdfAttachment = pdf;
        this.$store.commit(Mutations.SET_MESSAGE_ADMIN_ATTACHMENT_PDF, pdf);
    }

    public removeAdminAttachment() {
        this.fileData = null;
        this.adminAttachment64 = null;

        const input = this.$refs.fileInputElement as HTMLInputElement;
        input.type = 'text';
        input.type = 'file';

        this.$store.commit(Mutations.SET_MESSAGE_ADMIN_ATTACHMENT, null);
    }

    public removeAdminAttachmentPdf() {
        this.pdfAttachment = null;

        const pdfinput = this.$refs.pdfInputElement as HTMLInputElement;
        pdfinput.type = 'text';
        pdfinput.type = 'file';

        this.$store.commit(Mutations.SET_MESSAGE_ADMIN_ATTACHMENT_PDF, null);
    }

    public initializeKeyboardListener(): void {
        if (this.keyboardListenerAdded === true) {
            return;
        }

        if (this.$store.state.Viewport.deviceInfo.platform === 'web') {
            this.keyboardListenerAdded = true;
            return;
        }

        Keyboard.addListener('keyboardDidShow', (info: KeyboardInfo) => {
            this.keyboardVisible = true;

            this.goToBottom();
        });

        Keyboard.addListener('keyboardDidHide', () => {
            this.keyboardVisible = false;
        });

        this.keyboardListenerAdded = true;
    }

    private async requestMobileImage(): Promise<boolean> {
        const image = await Camera.getPhoto({
            quality: 90,
            allowEditing: false,
            correctOrientation: true,
            resultType: CameraResultType.DataUrl,
            source: CameraSource.Prompt,
        }).then((photo) => {
            let tempImage = new Image();
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');

            tempImage.onload = () => {
                const width = 1024;
                const widthProportion = width / tempImage.width;
                const height = tempImage.height * widthProportion;

                canvas.width = width;
                canvas.height = height;
                context.drawImage(tempImage, 0, 0, width, height);

                tempImage = null;

                this.$store.commit(Mutations.SAVE_CAPTURED_IMAGE_DATA, {
                    meta: {
                        width,
                        height,
                    },
                    data: canvas.toDataURL('image/jpeg'),
                });
            };

            tempImage.src = photo.dataUrl;
        });


        return true;
    }

    private dismissTakenImage(): void {
        this.$store.commit(Mutations.RESET_CAPTURED_IMAGE_DATA);
    }

    @Watch('conversation.messages')
    private onNewMessage(): void {
        Vue.nextTick(() => {
            this.scrollToBottom();
        });
    }

    private scrollToBottom(instant: boolean = false): void {
        if (this.scrollElement == null) {
            this.scrollElement = document.getElementById('hococo-messages-overflow');
        }

        if (instant === true) {
            this.scrollElement.scrollTo(0, this.scrollElement.scrollHeight);
        } else {
            this.scrollElement.scroll({
                top: this.scrollElement.scrollHeight,
                behavior: 'auto',
            });
        }
    }

    private scrollToMessage(messageId: number) {
        const element = document.getElementById('hococo-message-id-' + messageId);
        element.scrollIntoView();
    }
}
